import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, LogIn, Plus, Search, Check, Power, Pencil, CreditCard, MoreHorizontal, Trophy } from 'lucide-react';
import { getAllOrganizations, toggleOrganizationActive, updateOrganization, getAllSubscriptionPlans, getCurrentSubscription, setOrgSubscription } from '../../lib/supabase';
import { useOrganization } from '../../contexts/OrganizationContext';
import { FeaturedLayout } from '../layouts';
import OrganizationWizard from './OrganizationWizard';
import PlanBadge from '../ui/PlanBadge';
import { orgSinDeportes } from '../../lib/deportesOrg';
import { nombreAdministradora, ofreceEntrar } from '../../lib/orgAdministrada';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label
} from '../ui';
import { cn } from '../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../ui/dropdown-menu';
import { useUnsavedGuard } from '../../hooks/useUnsavedGuard';
import { ORG_TYPE_LABELS } from '../../constants/orgTypes';

// El Platform Admin gestiona TODAS las orgs de la plataforma (crear, suspender,
// branding, plan). Las organizadoras (league/tournament_organizer) operan en la app
// de Torneos, pero su administracion de plataforma vive aqui.
const PLATFORM_ORG_TYPES = ['club', 'academy', 'league', 'tournament_organizer'];

// CreateOrgModal reemplazado por OrganizationWizard (Fase 7)

const EditOrgModal = ({ isOpen, onClose, org, onUpdated }) => {
  const [form, setForm] = useState({ name: '', short_name: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const guard = useUnsavedGuard({ onClose });

  useEffect(() => {
    if (isOpen && org) {
      setForm({ name: org.name || '', short_name: org.short_name || '' });
      setError(null);
      setMessage(null);
      guard.resetDirty();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, org]);

  if (!isOpen || !org) return null;

  const applyChanges = async () => {
    setSaving(true);
    setError(null);

    const { error: updateErr } = await updateOrganization(org.id, {
      name: form.name.trim(),
      short_name: form.short_name.trim() || null,
    });

    if (updateErr) {
      setError(updateErr.message);
      setSaving(false);
      return;
    }

    setSaving(false);
    onUpdated({ ...org, name: form.name.trim(), short_name: form.short_name.trim() || null });
    guard.resetDirty();
    onClose();
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    applyChanges();
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={guard.onOpenChange}>
        <DialogContent {...guard.dirtyProps} className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Editar organización</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input
                value={form.name}
                onChange={(e) => { guard.markDirty(); setForm(prev => ({ ...prev, name: e.target.value })) }}
              />
            </div>

            <div className="space-y-2">
              <Label>Nombre corto</Label>
              <Input
                value={form.short_name}
                onChange={(e) => { guard.markDirty(); setForm(prev => ({ ...prev, short_name: e.target.value })) }}
                maxLength={50}
              />
            </div>

            <div className="border-t pt-4 text-sm text-muted-foreground">
              Los deportes se cambian en su propia pantalla (menú ⋯ → Deportes), con motivo e historial.
            </div>

            <div className="border-t pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <div className="text-muted-foreground">Tipo</div>
                <div className="capitalize">{org.type}</div>
                <div className="text-muted-foreground">Slug</div>
                <div className="font-mono">{org.slug}</div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => guard.onOpenChange(false)}>Cancelar</Button>
            <Button
              onClick={handleSave}
              disabled={saving || !form.name.trim()}
            >
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {guard.DiscardDialog}

    </>
  );
};

// Los planes son por app: club/academia usan planes de Club; liga/organizador, de Torneos.
const appOfOrgType = (type) => (type === 'league' || type === 'tournament_organizer' ? 'torneos' : 'club');

const ChangeOrgPlanModal = ({ isOpen, onClose, org, onUpdated }) => {
  const [plans, setPlans] = useState([]);
  const [currentSub, setCurrentSub] = useState(null);
  const [selectedPlanCode, setSelectedPlanCode] = useState('starter');
  const [grandfathered, setGrandfathered] = useState(true);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const guard = useUnsavedGuard({ onClose });

  useEffect(() => {
    if (!isOpen || !org) return;
    setError(null);
    setLoading(true);
    Promise.all([
      getAllSubscriptionPlans(),
      getCurrentSubscription(org.id),
    ]).then(([plansRes, subRes]) => {
      if (plansRes.data) setPlans(plansRes.data);
      const sub = subRes.data;
      setCurrentSub(sub);
      const defaultCode = appOfOrgType(org.type) === 'torneos' ? 'torneos_free' : 'starter';
      setSelectedPlanCode(sub?.plan?.code || defaultCode);
      setGrandfathered(sub?.grandfathered_forever ?? true);
      setReason(sub?.grandfathered_reason || '');
      setLoading(false);
      guard.resetDirty();
    }).catch((e) => {
      setError(e.message);
      setLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, org]);

  if (!isOpen || !org) return null;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const { data, error: saveError } = await setOrgSubscription(org.id, {
      planCode: selectedPlanCode,
      grandfathered,
      reason: grandfathered ? (reason.trim() || 'Asignado manualmente por platform admin') : null,
    });
    setSaving(false);
    if (saveError) {
      setError(saveError.message);
      return;
    }
    onUpdated(org.id, data);
    guard.resetDirty();
    onClose();
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={guard.onOpenChange}>
        <DialogContent {...guard.dirtyProps} className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cambiar plan</DialogTitle>
            <DialogDescription>
              Organización: <strong className="text-foreground">{org.name}</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {currentSub?.plan?.code && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                Plan actual:
                <PlanBadge plan={currentSub.plan.code} />
                {currentSub.grandfathered_forever && (
                  <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">
                    Sin cobro
                  </span>
                )}
              </div>
            )}

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {loading ? (
              <div className="text-sm text-muted-foreground py-4 text-center">Cargando...</div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Plan</Label>
                  <div className="space-y-2">
                    {plans.filter((p) => (p.app_key || 'club') === appOfOrgType(org.type)).map((p) => (
                      <label
                        key={p.id}
                        className={cn(
                          'flex items-start gap-3 p-3 border rounded-xl cursor-pointer transition-colors',
                          selectedPlanCode === p.code
                            ? 'border-primary bg-muted/50'
                            : 'hover:border-primary/40'
                        )}
                      >
                        <input
                          type="radio"
                          name="plan"
                          value={p.code}
                          checked={selectedPlanCode === p.code}
                          onChange={() => { guard.markDirty(); setSelectedPlanCode(p.code) }}
                          className="mt-1 accent-primary"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{p.name}</span>
                            <PlanBadge plan={p.code} />
                          </div>
                          {p.description && (
                            <div className="text-xs text-muted-foreground mt-0.5">{p.description}</div>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="border-t pt-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="grandfathered"
                      checked={grandfathered}
                      onCheckedChange={(v) => { guard.markDirty(); setGrandfathered(v) }}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <Label htmlFor="grandfathered" className="cursor-pointer">
                        Sin cobro (grandfathered)
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Marca esta org como exenta de cobro y enforcement de límites. Útil mientras no haya Stripe integrado.
                      </p>
                    </div>
                  </div>

                  {grandfathered && (
                    <div className="pl-7 space-y-2">
                      <Label className="text-xs">Razón (opcional)</Label>
                      <Input
                        value={reason}
                        onChange={(e) => { guard.markDirty(); setReason(e.target.value) }}
                        placeholder="Ej: Cliente fundador / cortesía / pre-lanzamiento"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => guard.onOpenChange(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving || loading}>
              {saving ? 'Guardando...' : 'Guardar plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {guard.DiscardDialog}
    </>
  );
};

const PlatformOrganizations = () => {
  const navigate = useNavigate();
  const { switchOrg, resetOrg, org: currentOrg, isOrgOverridden } = useOrganization();
  const [orgs, setOrgs] = useState([]);
  const [orgPlans, setOrgPlans] = useState({}); // orgId -> { code, grandfathered }
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [appFilter, setAppFilter] = useState('todos'); // todos | club | torneos
  const [showCreate, setShowCreate] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [planningOrg, setPlanningOrg] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const loadOrgs = async () => {
    const { data } = await getAllOrganizations();
    if (data) {
      const visibles = data.filter(o => PLATFORM_ORG_TYPES.includes(o.type));
      setOrgs(visibles);
      // Cargar plan actual de cada org en paralelo
      const plansMap = {};
      await Promise.all(
        visibles.map(async (o) => {
          const { data: sub } = await getCurrentSubscription(o.id);
          if (sub?.plan?.code) {
            plansMap[o.id] = {
              code: sub.plan.code,
              grandfathered: sub.grandfathered_forever === true,
            };
          }
        })
      );
      setOrgPlans(plansMap);
    }
    setLoading(false);
  };

  useEffect(() => { loadOrgs(); }, []);

  const handleEnterOrg = (orgId) => {
    switchOrg(orgId);
    navigate('/groups');
  };

  const handleReturnToMyOrg = () => {
    resetOrg();
    navigate('/platform');
  };

  const handleToggleActive = async (org) => {
    setTogglingId(org.id);
    const { data } = await toggleOrganizationActive(org.id, !org.active);
    if (data) {
      setOrgs(prev => prev.map(o => o.id === org.id ? { ...o, active: data.active } : o));
    }
    setTogglingId(null);
  };

  const handleOrgUpdated = (updatedOrg) => {
    setOrgs(prev => prev.map(o => o.id === updatedOrg.id ? { ...o, ...updatedOrg } : o));
  };

  const handlePlanUpdated = (orgId, subscription) => {
    if (!subscription?.plan?.code) return;
    setOrgPlans(prev => ({
      ...prev,
      [orgId]: {
        code: subscription.plan.code,
        grandfathered: subscription.grandfathered_forever === true,
      },
    }));
  };

  const handleOrgCreated = (newOrg) => {
    setOrgs(prev => [...prev, newOrg].sort((a, b) => a.name.localeCompare(b.name)));
  };

  const filtered = orgs.filter(o =>
    (appFilter === 'todos' || appOfOrgType(o.type) === appFilter) &&
    (o.name.toLowerCase().includes(search.toLowerCase()) ||
      o.slug.toLowerCase().includes(search.toLowerCase()))
  );
  const countClub = orgs.filter(o => appOfOrgType(o.type) === 'club').length;
  const countTorneos = orgs.filter(o => appOfOrgType(o.type) === 'torneos').length;

  return (
    <FeaturedLayout title="Organizaciones"
      refreshAction={loadOrgs}>
      <div className="max-w-4xl mx-auto space-y-4">

        {/* Toolbar */}
        <div className="flex items-center gap-2 flex-nowrap">
          <div className="flex-1 min-w-0 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar organización..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground hidden sm:block whitespace-nowrap">
              <span className="font-medium text-foreground">{filtered.length}</span>{' '}
              {filtered.length === 1 ? 'organización' : 'organizaciones'}
            </span>
            <Button onClick={() => setShowCreate(true)} className="h-10" aria-label="Nueva organización" title="Nueva organización">
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Nueva organización</span>
            </Button>
          </div>
        </div>

        {/* Filtro por app: Clubes vs Torneos (para no mezclarlas) */}
        <div className="flex flex-wrap items-center gap-1.5">
          {[
            { value: 'todos', label: 'Todas', count: orgs.length },
            { value: 'club', label: 'Clubes', count: countClub },
            { value: 'torneos', label: 'Torneos', count: countTorneos },
          ].map((f) => (
            <Button
              key={f.value}
              variant={appFilter === f.value ? 'default' : 'outline'}
              size="sm"
              className="h-8 rounded-full px-3 text-xs font-normal"
              onClick={() => setAppFilter(f.value)}
            >
              {f.label}
              <span className={cn('ml-1.5 tabular-nums', appFilter !== f.value && 'text-muted-foreground')}>{f.count}</span>
            </Button>
          ))}
        </div>

        {/* Resumen */}
        <div className="flex gap-4 text-sm text-muted-foreground">
          <span>{orgs.filter(o => o.active).length} activas</span>
          <span>{orgs.filter(o => !o.active).length} suspendidas</span>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center text-muted-foreground text-sm py-8">Cargando...</div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Building2 className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
              <p className="text-muted-foreground mb-2">
                {orgs.length === 0 ? 'Todavía no hay organizaciones' : 'No se encontraron organizaciones'}
              </p>
              {orgs.length === 0 && (
                <Button size="sm" className="mt-2" onClick={() => setShowCreate(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Crear la primera
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filtered.map((org) => {
              // Las organizadoras (league/tournament_organizer) operan en la app de
              // Torneos y los clubes administrados no tienen acceso a la app: no tiene
              // sentido "Entrar". Se gestionan (editar/plan/suspender) pero no se
              // impersonan aqui (ver ofreceEntrar).
              const administradora = nombreAdministradora(org, orgs);
              return (
              <div
                key={org.id}
                className={`bg-card rounded-xl border p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4 transition-colors ${
                  org.active ? 'hover:border-primary/40' : 'border-destructive/30 bg-destructive/5 opacity-75'
                }`}
              >
                {/* Logo + info (fila propia en movil; comparten fila con acciones en sm+) */}
                <div className="flex items-center gap-3 min-w-0 sm:flex-1">
                {/* Logo */}
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden"
                  style={{ backgroundColor: org.color_primary || '#000' }}
                >
                  {org.logo_url ? (
                    <img src={org.logo_url} alt={org.name} className="w-8 h-8 object-contain" />
                  ) : (
                    <Building2 size={20} style={{ color: org.color_background || '#fff' }} />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate flex items-center gap-2">
                    <span className="truncate">{org.name}</span>
                    {orgPlans[org.id]?.code && (
                      <PlanBadge plan={orgPlans[org.id].code} />
                    )}
                    {orgPlans[org.id]?.grandfathered && (
                      <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap">
                        Sin cobro
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground flex items-center gap-2">
                    <span className="capitalize">{ORG_TYPE_LABELS[org.type] || org.type}</span>
                    <span className="text-border">|</span>
                    <span className="font-mono text-xs">{org.slug}</span>
                    {!org.active && (
                      <Badge variant="outline" className="text-[10px] font-normal text-destructive border-destructive/30">
                        Suspendida
                      </Badge>
                    )}
                    {orgSinDeportes(org) && (
                      <Badge variant="outline" className="text-[10px] font-normal text-amber-700 border-amber-300">
                        Sin deportes
                      </Badge>
                    )}
                    {administradora !== null && (
                      <Badge variant="outline" className="text-[10px] font-normal text-sky-700 border-sky-300">
                        {administradora ? `Administrado por ${administradora}` : 'Administrado'}
                      </Badge>
                    )}
                  </div>
                </div>
                </div>

                {/* Actions (segunda fila en movil, alineadas a la derecha) */}
                <div className="flex items-center gap-2 justify-end sm:flex-shrink-0">
                  {/* Entrar = la accion principal de esta pantalla (no aplica a organizadoras ni a clubes administrados) */}
                  {ofreceEntrar(org) && (
                    currentOrg?.id === org.id ? (
                      <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-3 py-1.5 rounded-lg">
                        <Check size={14} />
                        Actual
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        className="h-8"
                        onClick={() => handleEnterOrg(org.id)}
                        disabled={!org.active}
                      >
                        <LogIn className="h-3.5 w-3.5 mr-1.5" />
                        Entrar
                      </Button>
                    )
                  )}

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" title="Más acciones">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setEditingOrg(org)}>
                        <Pencil className="h-4 w-4" />
                        Editar organización
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate(`/platform/organizations/${org.id}/deportes`)}>
                        <Trophy className="h-4 w-4" />
                        Deportes
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setPlanningOrg(org)}>
                        <CreditCard className="h-4 w-4" />
                        Cambiar plan
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {/* Suspender corta el acceso de todo el club: fuera del nivel principal */}
                      <DropdownMenuItem
                        onClick={() => handleToggleActive(org)}
                        disabled={togglingId === org.id}
                        className={org.active ? 'text-destructive focus:text-destructive' : ''}
                      >
                        <Power className="h-4 w-4" />
                        {org.active ? 'Suspender acceso' : 'Reactivar acceso'}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              );
            })}
          </div>
        )}

        <OrganizationWizard
          isOpen={showCreate}
          onClose={() => setShowCreate(false)}
          onCreated={handleOrgCreated}
        />

        <EditOrgModal
          isOpen={!!editingOrg}
          org={editingOrg}
          onClose={() => setEditingOrg(null)}
          onUpdated={handleOrgUpdated}
        />

        <ChangeOrgPlanModal
          isOpen={!!planningOrg}
          org={planningOrg}
          onClose={() => setPlanningOrg(null)}
          onUpdated={handlePlanUpdated}
        />
      </div>
    </FeaturedLayout>
  );
};

export default PlatformOrganizations;
